import React, { useMemo } from 'react';
import {
    DataGridPro,
    DataGridProProps,
} from '@mui/x-data-grid-pro';
import {
    buildCustomGrid,
    gridAlignCentre,
    gridDateComparer,
    gridDateOperators,
    gridDateRenderer
} from "../../../util/grid";
import CustomGridToolbar from "../CustomGridToolbar";
import { Box } from "@mui/material";
import { GridColumnDef, GridColumnTemplate } from "../grid";
import { GridColumnVisibilityModel } from "@mui/x-data-grid/hooks/features/columns/gridColumnsInterfaces";
import { setColumnVisibility } from "../../../features/order/orderSlice";
import { useAppDispatch, useAppSelector } from "../../../util/hooks";
import { RootState } from "../../../util/store";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

export interface OrderGridProps extends Omit<DataGridProProps, 'columns'> {
    columnDef: GridColumnDef[];
}

export default function OrderGrid(props: OrderGridProps): JSX.Element {
    const {
        columnDef,
        ...rest
    } = props;

    const dispatch = useAppDispatch();
    const columnVisibility = useAppSelector((state: RootState) => state.order.columnVisibility);

    const columnTemplate:GridColumnTemplate = useMemo(() => {
        return {
            id: {field: 'id', headerName: 'ID', width: 120, hide: true},
            orderDetails: {
                field: 'orderDetails',
                headerName: 'Order Details',
                width: 50,
                disableColumnMenu: true,
                disableReorder: true,
                filterable: false,
                hideable: false,
                resizable: false,
                sortable: false,
                align: gridAlignCentre,
                headerAlign: gridAlignCentre,
                renderHeader: () => (
                    <FontAwesomeIcon size='lg' icon={['fas', 'file']} title='Order Details'/>
                ),
                renderCell: () => {
                    return (
                        <FontAwesomeIcon
                            size='lg'
                            icon={['fas', 'file']}
                            title='Order Summary'
                            style={{cursor: 'pointer'}}
                        />
                    );
                },
            },
            batchOrderNumber: {field: 'batchOrderNumber', headerName: 'Batch Order Number', width: 210},
            trackingNumber: {field: 'trackingNumber', headerName: 'Tracking Number', width: 190},
            serviceType: {
                field: 'serviceType',
                headerName: 'Service',
                width: 150,
                type: 'singleSelect',
                valueOptions: [
                    "Delivery",
                    "Pickup",
                    "Receive",
                    "Scan on Demand",
                    "Permout",
                    "Other Services",
                    "Sell",
                ],
            },
            details: {field: 'details', headerName: 'Details', width: 150},
            quantity: {field: 'quantity', headerName: 'Quantity', width: 150},
            status: {
                field: 'status',
                headerName: 'Status',
                width: 150,
                type: 'singleSelect',
                valueOptions: [
                    "Submitted",
                    "Scheduled",
                    "Workorder",
                    "Fulfilled",
                    "OnHold",
                    "Cancelled",
                    "AwaitingApproval",
                    "Rejected",
                ],
            },
            statusDate: {
                field: 'statusDate',
                headerName: 'Status Date',
                width: 150,
                type: 'date',
                sortComparator: gridDateComparer,
                filterOperators: gridDateOperators,
                renderCell: gridDateRenderer,
            },
            workorderID: {field: 'workorderID', headerName: 'Work Order #', width: 150},
            requestor: {field: 'requestor', headerName: 'Requestor', width: 200},
            orderedBy: {field: 'orderedBy', headerName: 'Ordered by', width: 200},
            customerWorkorderAccount: {field: 'customerWorkorderAccount', headerName: 'Work Order Account', width: 150},
            orderDate: {
                field: 'orderDate',
                headerName: 'Order Date',
                width: 150,
                type: 'date',
                sortComparator: gridDateComparer,
                filterOperators: gridDateOperators,
                renderCell: gridDateRenderer,
            },
        }
    }, []);

    const columns = useMemo(() => {
        return buildCustomGrid(columnDef, columnTemplate);
    }, [columnDef, columnTemplate]);

    const handleColumnVisibilityChange = (model: GridColumnVisibilityModel) => {
        dispatch(setColumnVisibility({
            ...model,
            id: false,
            orderDetails: true,
        }));
    }

    return (
        <Box sx={{ height: '90vh' }}>
            <DataGridPro
                {...rest}
                columns={columns}
                disableSelectionOnClick
                columnVisibilityModel={columnVisibility}
                onColumnVisibilityModelChange={handleColumnVisibilityChange}
                components={{
                    Toolbar: CustomGridToolbar,
                }}
                localeText={{
                    toolbarExport: 'Export grid results',
                    toolbarExportLabel: 'Export grid results',
                }}
            />
        </Box>
    );
}
